// The funnel: how many leads are at each stage, in which cities they are and how many of those became customers, and which
// message got replies. The charts are the kit's ranked bars (a list with a bar and the number at its end), so every figure is
// text first; the two tables carry the rest.

import type { LeadFunnel, LeadStage } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { BarChart, EmptyState, Skeleton, StatCard, Table, type BarDatum, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { Section, SummaryStrip } from '../../ui';
import { RequirePlatformPermission } from '../access';
import { HQ_LEADS } from '../sections';
import { OPEN_STAGES } from './labels';
import { LeadsHeader } from './LeadsHeader';
import { useLeadFunnel } from './queries';
import '../hq.css';

export function FunnelPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_LEADS}>
      <Funnel />
    </RequirePlatformPermission>
  );
}

type CityRow = LeadFunnel['byCity'][number];
type TemplateRow = LeadFunnel['byTemplate'][number];

function Funnel(): ReactNode {
  const { t } = useTranslation('hq');
  const funnel = useLeadFunnel();

  let body: ReactNode;
  if (funnel.data === undefined) {
    body = funnel.isError ? (
      <LoadFailure error={funnel.error} message={t('leads.funnel.unavailable')} onRetry={() => void funnel.refetch()} />
    ) : (
      <div aria-busy="true" className="console-lead-today__skeleton">
        <Skeleton height="4rem" />
        <Skeleton height="14rem" />
        <Skeleton height="10rem" />
      </div>
    );
  } else if (funnel.data.totals.leads === 0) {
    body = <EmptyState icon={<NavIcon name="leads" />} title={t('leads.funnel.empty')} description={t('leads.funnel.emptyHint')} />;
  } else {
    body = <FunnelBody funnel={funnel.data} />;
  }

  return <LeadsHeader current="funnel">{body}</LeadsHeader>;
}

function FunnelBody({ funnel }: { readonly funnel: LeadFunnel }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const counts = new Map(funnel.byStage.map((row) => [row.stage, row.count] as const));
  const scale = Math.max(1, ...funnel.byStage.map((row) => row.count));
  const bar = (stage: LeadStage): BarDatum => ({
    key: stage,
    label: t(`leads.stage.${stage}`),
    value: counts.get(stage) ?? 0,
    display: counts.get(stage) ?? 0,
    ...(stage === 'won' ? { tone: 2 as const } : {}),
  });
  const pipelineStages = [...OPEN_STAGES, 'won' as const];
  const percent = (part: number, whole: number): string => (whole === 0 ? t('leads.funnel.noRate') : format.percent(part / whole, 0));

  const cityColumns: readonly TableColumn<CityRow>[] = [
    { key: 'city', header: t('leads.funnel.city'), cell: (row) => row.city },
    { key: 'total', header: t('leads.funnel.total'), numeric: true, cell: (row) => row.total },
    { key: 'open', header: t('leads.funnel.open'), numeric: true, cell: (row) => row.open },
    { key: 'won', header: t('leads.funnel.won'), numeric: true, cell: (row) => row.won },
    { key: 'conversion', header: t('leads.funnel.conversion'), numeric: true, cell: (row) => percent(row.won, row.total) },
  ];
  const templateColumns: readonly TableColumn<TemplateRow>[] = [
    { key: 'template', header: t('leads.funnel.template'), cell: (row) => row.templateName ?? t('leads.funnel.noTemplate') },
    { key: 'channel', header: t('leads.funnel.channel'), cell: (row) => t(`leads.channel.${row.channel}`) },
    { key: 'sent', header: t('leads.funnel.sent'), numeric: true, cell: (row) => row.sent },
    { key: 'replied', header: t('leads.funnel.replied'), numeric: true, cell: (row) => row.replied },
    { key: 'rate', header: t('leads.funnel.replyRate'), numeric: true, cell: (row) => percent(row.replied, row.sent) },
  ];

  return (
    <>
      <SummaryStrip label={t('leads.funnel.title')}>
        <StatCard label={t('leads.funnel.totals.leads')} value={funnel.totals.leads} />
        <StatCard label={t('leads.funnel.totals.contacted')} value={funnel.totals.contacted} hint={percent(funnel.totals.contacted, funnel.totals.leads)} />
        <StatCard label={t('leads.funnel.totals.replied')} value={funnel.totals.replied} hint={percent(funnel.totals.replied, funnel.totals.contacted)} />
        <StatCard label={t('leads.funnel.totals.won')} value={funnel.totals.won} hint={percent(funnel.totals.won, funnel.totals.leads)} />
      </SummaryStrip>
      <Section title={t('leads.funnel.stagesTitle')}>
        <BarChart label={t('leads.funnel.stagesLabel')} rows={pipelineStages.map(bar)} max={scale} />
        <h3 className="console-lead-funnel__sub">{t('leads.funnel.outOfPlay')}</h3>
        <BarChart label={t('leads.funnel.outOfPlayLabel')} rows={[bar('lost'), bar('parked')]} max={scale} />
      </Section>
      <Section title={t('leads.funnel.citiesTitle')} body="rows">
        <Table caption={t('leads.funnel.citiesCaption')} captionHidden columns={cityColumns} rows={funnel.byCity} rowKey={(row) => row.city} empty={t('leads.funnel.noCities')} />
      </Section>
      <Section title={t('leads.funnel.templatesTitle')} body="rows">
        <Table caption={t('leads.funnel.templatesCaption')} captionHidden columns={templateColumns} rows={funnel.byTemplate} rowKey={(row) => `${row.templateId ?? 'none'}-${row.channel}`} empty={t('leads.funnel.noTemplates')} />
      </Section>
    </>
  );
}
